import * as React from "react";
import { Plus, Pencil, Trash2, Eye, Loader2, Printer } from "lucide-react";
import { cn } from "../utils";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger" | "ghost" | "link";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      isLoading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "inline-flex items-center justify-center font-medium transition focus:outline-none disabled:opacity-50 disabled:pointer-events-none rounded-lg";

    const variants = {
      primary: "bg-primary text-white hover:bg-darkprimary",
      secondary: "bg-secondary text-dark hover:opacity-90",
      danger: "bg-red-600 text-white hover:bg-red-700",
      ghost: "text-dark hover:bg-lightprimary/20",
      link: "text-primary hover:underline p-0 bg-transparent",
    };

    const sizes = {
      sm: "text-xs px-2.5 py-1.5 gap-1.5",
      md: "text-sm px-4 py-2 gap-2",
      lg: "text-base px-5 py-2.5 gap-2",
    };

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        {...props}
      >
        {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";

// Action Buttons
export const AddButton = ({
  label,
  isLoading,
  ...props
}: ButtonProps & { label: string }) => (
  <Button variant="primary" isLoading={isLoading} {...props}>
    {!isLoading && <Plus className="w-4 h-4" />}
    {label}
  </Button>
);

export const EditButton = ({
  label = "Τροποποίηση",
  isLoading,
  ...props
}: ButtonProps & { label?: string }) => (
  <Button
    variant="ghost"
    size="sm"
    title="Τροποποίηση εγγραφής"
    isLoading={isLoading}
    className="text-primary hover:bg-lightprimary/30 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap"
    {...props}
  >
    {!isLoading && <Pencil className="w-4 h-4" />}
    {label}
  </Button>
);

export const ViewButton = ({
  label = "Προβολή",
  isLoading,
  ...props
}: ButtonProps & { label?: string }) => (
  <Button
    variant="link"
    size="sm"
    title="Προβολή"
    isLoading={isLoading}
    className="opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap"
    {...props}
  >
    {!isLoading && <Eye className="w-4 h-4" />}
    {label}
  </Button>
);

export const PrintButton = ({
  label = "Εκτύπωση",
  isLoading,
  ...props
}: ButtonProps & { label?: string }) => (
  <Button
    variant="secondary"
    size="md"
    title="Εκτύπωση"
    isLoading={isLoading}
    {...props}
  >
    {!isLoading && <Printer className="w-4 h-4" />}
    {label}
  </Button>
);

export const DeleteButton = ({
  label = "Διαγραφή",
  isLoading,
  onClick,
  ...props
}: ButtonProps & { label?: string }) => (
  <Button
    variant="link"
    size="sm"
    title="Διαγραφή εγγραφής"
    isLoading={isLoading}
    className="text-red-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap"
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(e);
    }}
    {...props}
  >
    {!isLoading && <Trash2 className="w-4 h-4" />}
    {label}
  </Button>
);

export interface CheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, ...props }, ref) => {
    return (
      <input
        type="checkbox"
        ref={ref}
        className={cn(
          "w-4 h-4 rounded border-slate-300 text-primary accent-primary focus:ring-primary cursor-pointer transition-colors",
          className
        )}
        {...props}
      />
    );
  }
);
Checkbox.displayName = "Checkbox";